import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authStorage } from "../lib/storage/authStorage";
import { apiClient } from "../lib/http/apiClient";
import { dmSocket } from "../lib/realtime/dmSocket";
import { formatISTChatListTime, parseInstant } from "../utils/dateUtils";
import NotificationBell from "../components/NotificationBell";
import VerificationRequiredModal from "../components/VerificationRequiredModal";
import "./DirectChatPage.styles.css";
import { useBlobUrl, openAsBlob } from "../hooks/useBlobUrl";

const ACCEPTED_ATTACHMENT_TYPES =
    "image/jpeg,image/png,image/webp,image/gif,application/pdf," +
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
    "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
    "text/csv,text/plain";

interface Attachment {
    s3_key: string;
    filename: string;
    content_type: string;
    size: number;
    url?: string | null;
}

/** `att.url` is a presigned S3 URL — opened/rendered via a blob object URL
 * (see useBlobUrl) so the signed URL never appears in a new tab's address
 * bar or via "copy image address"/"copy link" on the attachment. */
function ChatAttachment({ attachment }: { attachment: Attachment }) {
    const isImage = attachment.content_type.startsWith("image/");
    const blobUrl = useBlobUrl(attachment.url);
    const open = () => attachment.url && void openAsBlob(attachment.url);

    if (isImage) {
        return (
            <button type="button" onClick={open} disabled={!attachment.url}>
                {blobUrl ? (
                    <img src={blobUrl} alt={attachment.filename} className="max-h-48 max-w-full rounded-md object-cover" />
                ) : (
                    <span className="flex h-24 w-24 items-center justify-center rounded-md bg-black/20 text-xs">…</span>
                )}
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={open}
            disabled={!attachment.url}
            className="flex items-center gap-1.5 rounded-md bg-black/20 px-2 py-1 text-xs underline"
        >
            📄 {attachment.filename}
        </button>
    );
}

interface Message {
    id: string;
    thread_id: string;
    sender_id: string;
    sender_name: string;
    sender_role: string;
    content: string;
    created_at: string;
    read: boolean;
    client_msg_id?: string | null;
    status?: "pending" | "sent" | "delivered" | "read" | "failed" | "received";
    attachments?: Attachment[];
}

interface Thread {
    id: string;
    buyer_id: string;
    buyer_name: string;
    seller_id: string;
    seller_name: string;
    deal_id?: string;
    deal_title?: string;
    last_message?: string;
    last_message_at?: string;
    unread_count: number;
    created_at: string;
    updated_at: string;
}

const sortByLatest = (items: Thread[]) =>
    [...items].sort((a, b) => {
        const aTs = parseInstant(a.last_message_at ?? a.updated_at)?.getTime() ?? 0;
        const bTs = parseInstant(b.last_message_at ?? b.updated_at)?.getTime() ?? 0;
        return bTs - aTs;
    });

export default function DirectChatPage() {
    const { threadId } = useParams<{ threadId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [threads, setThreads] = useState<Thread[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | null>(threadId ?? null);
    const [activeThread, setActiveThread] = useState<Thread | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [showSidebar, setShowSidebar] = useState(true);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [attachmentError, setAttachmentError] = useState<string>("");

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const activeThreadIdRef = useRef<string | null>(activeThreadId);
    const threadsRef = useRef<Thread[]>([]);
    const [wsConnected, setWsConnected] = useState(false);
    const [error, setError] = useState<string>("");
    const [verificationModalMessage, setVerificationModalMessage] = useState<string>("");
    const [typingName, setTypingName] = useState<string>("");
    const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
    const typingStopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const userId = user?.id ?? "";
    useEffect(() => {
        activeThreadIdRef.current = activeThreadId;
    }, [activeThreadId]);
    useEffect(() => {
        threadsRef.current = threads;
    }, [threads]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const normalizeServerMessage = useCallback((message: Message): Message => {
        const isMine = message.sender_id === userId;
        if (isMine) {
            return {
                ...message,
                status: message.read ? "read" : message.status ?? "sent",
            };
        }
        return {
            ...message,
            status: "received",
        };
    }, [userId]);

    // Delegates to the shared ref-counted DM socket. This page used to own its
    // own WebSocket, which meant a user on the chat page held two DM
    // connections (this one plus DmGlobalListener's) — both authenticating,
    // both registering presence, and both handling every broadcast. The hub
    // also queues frames sent while reconnecting, which the raw socket dropped.
    const sendWs = useCallback((payload: Record<string, unknown>) => {
        dmSocket.send(payload);
    }, []);

    useEffect(() => {
        const token = authStorage.getToken();
        if (!token) {
            setLoadingThreads(false);
            return;
        }

        let isCancelled = false;
        const onEvent = (payload: Record<string, unknown>) => {
            if (isCancelled) return;
            const type = payload.type as string;

            if (type === "dm:connected") {
                setWsConnected(true);
                setError("");
                setLoadingThreads(false);
                sendWs({ type: "dm:threads:list", limit: 100, skip: 0 });
                if (activeThreadIdRef.current) {
                    setLoadingMessages(true);
                    sendWs({ type: "dm:thread:messages", thread_id: activeThreadIdRef.current, limit: 100 });
                }
                return;
            }

            if (type === "dm:disconnected") {
                setWsConnected(false);
                setError("Realtime connection failed. Reconnecting...");
                return;
            }

            // dm:ping/dm:pong is handled inside the hub — every
            // subscriber replying would send one pong per subscriber.

            if (type === "dm:error") {
                const errMessage = String(payload.message ?? "Chat error");
                const errCode = String(payload.code ?? "");
                const clientMsgId = String(payload.client_msg_id ?? "");

                if (clientMsgId) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === clientMsgId || m.client_msg_id === clientMsgId
                                ? { ...m, status: "failed" as const }
                                : m,
                        ),
                    );
                }

                setError(errMessage);
                if (errCode === "NOT_VERIFIED") {
                    setVerificationModalMessage(errMessage);
                }
                return;
            }

            if (type === "dm:presence:snapshot") {
                const ids = (payload.online_user_ids as string[]) ?? [];
                setOnlineUserIds(new Set(ids));
                return;
            }

            if (type === "dm:presence") {
                const presenceUserId = String(payload.user_id ?? "");
                const isOnline = Boolean(payload.is_online);
                if (!presenceUserId) return;
                setOnlineUserIds((prev) => {
                    const next = new Set(prev);
                    if (isOnline) {
                        next.add(presenceUserId);
                    } else {
                        next.delete(presenceUserId);
                    }
                    return next;
                });
                return;
            }

            if (type === "dm:threads:list") {
                const nextThreads = sortByLatest((payload.threads as Thread[]) ?? []);
                setThreads(nextThreads);
                setActiveThread((prev) => {
                    const id = activeThreadIdRef.current ?? prev?.id;
                    if (!id) return null;
                    return nextThreads.find((t) => t.id === id) ?? prev ?? null;
                });
                return;
            }

            if (type === "dm:thread:messages") {
                const threadId = String(payload.thread_id ?? "");
                if (!threadId || threadId !== activeThreadIdRef.current) return;
                const nextMessages = ((payload.messages as Message[]) ?? [])
                    .map((message) => normalizeServerMessage(message));
                setMessages(nextMessages);
                setLoadingMessages(false);
                setTimeout(scrollToBottom, 80);
                sendWs({ type: "dm:thread:active", thread_id: threadId });
                sendWs({ type: "dm:thread:read", thread_id: threadId });
                setThreads((prev) =>
                    prev.map((t) => (t.id === threadId ? { ...t, unread_count: 0 } : t)),
                );
                return;
            }

            if (type === "dm:message:ack") {
                const clientMsgId = String(payload.client_msg_id ?? "");
                const serverMessage = normalizeServerMessage(payload.message as Message);
                if (!clientMsgId || !serverMessage) return;
                setMessages((prev) =>
                    prev.map((m) => {
                        if (m.id === clientMsgId || m.client_msg_id === clientMsgId) {
                            const pOld = m.status === "read" ? 3 : m.status === "delivered" ? 2 : m.status === "sent" ? 1 : 0;
                            const pNew = serverMessage.status === "read" ? 3 : serverMessage.status === "delivered" ? 2 : serverMessage.status === "sent" ? 1 : 0;
                            return {
                                ...serverMessage,
                                status: pOld > pNew ? m.status : serverMessage.status,
                            };
                        }
                        return m;
                    }),
                );
                return;
            }

            if (type === "dm:message:status") {
                const status = String(payload.status ?? "") as Message["status"];
                const messageIds = ((payload.message_ids as string[]) ?? []);
                if (!status || !messageIds.length) return;
                setMessages((prev) =>
                    prev.map((message) => {
                        if (!messageIds.includes(message.id)) return message;
                        const pOld = message.status === "read" ? 3 : message.status === "delivered" ? 2 : message.status === "sent" ? 1 : 0;
                        const pNew = status === "read" ? 3 : status === "delivered" ? 2 : status === "sent" ? 1 : 0;
                        return {
                            ...message,
                            status: pOld > pNew ? message.status : status,
                        };
                    }),
                );
                return;
            }

            if (type === "dm:message:new") {
                const threadId = String(payload.thread_id ?? "");
                const message = normalizeServerMessage(payload.message as Message);
                if (!threadId || !message) return;

                // Brand-new thread (e.g. a freshly-accepted enquiry): the
                // list doesn't know it yet, so the message below would be
                // dropped. Pull a fresh thread list so it surfaces live.
                if (!threadsRef.current.some((t) => t.id === threadId)) {
                    sendWs({ type: "dm:threads:list", limit: 100, skip: 0 });
                }

                setThreads((prev) => {
                    const existing = prev.find((t) => t.id === threadId);
                    const next = existing
                        ? prev.map((t) => {
                              if (t.id !== threadId) return t;
                              const isIncomingUnread =
                                  message.sender_id !== userId && activeThreadIdRef.current !== threadId;
                              return {
                                  ...t,
                                  last_message: message.content,
                                  last_message_at: message.created_at,
                                  updated_at: message.created_at,
                                  unread_count: isIncomingUnread ? t.unread_count + 1 : 0,
                              };
                          })
                        : prev;
                    return sortByLatest(next);
                });

                if (message.sender_id !== userId) {
                    sendWs({
                        type: "dm:message:delivered",
                        thread_id: threadId,
                        message_id: message.id,
                    });
                }

                if (threadId !== activeThreadIdRef.current) return;
                
                if (message.sender_id !== userId) {
                    sendWs({ type: "dm:thread:read", thread_id: threadId });
                }

                setMessages((prev) => {
                    const existingIdx = prev.findIndex(
                        (m) =>
                            m.id === message.id ||
                            (message.client_msg_id && m.id === message.client_msg_id) ||
                            (message.client_msg_id && m.client_msg_id === message.client_msg_id),
                    );
                    if (existingIdx >= 0) {
                        const updated = [...prev];
                        const m = updated[existingIdx];
                        const pOld = m.status === "read" ? 3 : m.status === "delivered" ? 2 : m.status === "sent" ? 1 : 0;
                        const pNew = message.status === "read" ? 3 : message.status === "delivered" ? 2 : message.status === "sent" ? 1 : 0;
                        updated[existingIdx] = {
                            ...message,
                            status: pOld > pNew ? m.status : message.status,
                        };
                        return updated;
                    }
                    return [...prev, message];
                });
                if (message.sender_id !== userId) {
                    sendWs({ type: "dm:thread:read", thread_id: threadId });
                }
                setTimeout(scrollToBottom, 60);
                return;
            }

            if (type === "dm:messages:missed") {
                const missed = ((payload.messages as Message[]) ?? [])
                    .map((message) => normalizeServerMessage(message));
                if (!missed.length) return;
                const activeId = activeThreadIdRef.current;
                if (!activeId) return;
                const forActive = missed.filter((message) => message.thread_id === activeId);
                if (!forActive.length) return;
                setMessages((prev) => {
                    const seen = new Set(prev.map((message) => message.id));
                    const merged = [...prev];
                    for (const message of forActive) {
                        if (!seen.has(message.id)) {
                            merged.push(message);
                        }
                    }
                    merged.sort(
                        (a, b) =>
                            (parseInstant(a.created_at)?.getTime() ?? 0) -
                            (parseInstant(b.created_at)?.getTime() ?? 0),
                    );
                    return merged;
                });
                setTimeout(scrollToBottom, 60);
                return;
            }

            if (type === "dm:typing") {
                const threadId = String(payload.thread_id ?? "");
                const fromUserId = String(payload.user_id ?? "");
                const fromUserName = String(payload.user_name ?? "Someone");
                const isTyping = Boolean(payload.is_typing);
                if (
                    threadId !== activeThreadIdRef.current ||
                    !fromUserId ||
                    fromUserId === userId
                ) {
                    return;
                }
                setTypingName(isTyping ? fromUserName : "");
            }
        };

        const unsubscribe = dmSocket.subscribe(onEvent);
        // subscribe() opens the socket if it isn't already up; if it was
        // already open (DmGlobalListener holds a reference app-wide) no
        // dm:connected fires for this late subscriber, so seed from the
        // hub's current state instead of waiting for a reconnect.
        if (dmSocket.connected) {
            setWsConnected(true);
            setLoadingThreads(false);
            sendWs({ type: "dm:threads:list", limit: 100, skip: 0 });
            if (activeThreadIdRef.current) {
                setLoadingMessages(true);
                sendWs({ type: "dm:thread:messages", thread_id: activeThreadIdRef.current, limit: 100 });
            }
        }

        return () => {
            isCancelled = true;
            if (typingStopTimeoutRef.current) clearTimeout(typingStopTimeoutRef.current);
            unsubscribe();
        };
    }, [normalizeServerMessage, sendWs, scrollToBottom, userId]);

    // Set active thread from URL param
    useEffect(() => {
        if (threadId && threadId !== activeThreadId) {
            setActiveThreadId(threadId);
        }
    }, [threadId, activeThreadId]);

    useEffect(() => {
        if (!activeThreadId) return;
        const thread = threads.find((t) => t.id === activeThreadId);
        setActiveThread(thread ?? null);
    }, [activeThreadId, threads]);

    useEffect(() => {
        if (!activeThreadId) return;
        if (!wsConnected) return;
        setLoadingMessages(true);
        sendWs({ type: "dm:thread:active", thread_id: activeThreadId });
        sendWs({ type: "dm:thread:messages", thread_id: activeThreadId, limit: 100 });
    }, [activeThreadId, wsConnected, sendWs]);

    useEffect(() => {
        if (!wsConnected || activeThreadId) return;
        sendWs({ type: "dm:thread:active", thread_id: "" });
    }, [activeThreadId, wsConnected, sendWs]);

    // Pushed dm:presence events have no TTL of their own: if the server's
    // offline broadcast is ever dropped, the "Online" badge can stay stuck
    // long after the other party actually disconnected. Periodically re-sync
    // against the server's live presence store so the badge self-corrects
    // instead of trusting a single cached push indefinitely.
    //
    // This is a safety net, not the primary mechanism — presence changes are
    // pushed. It used to fire every 15s unconditionally, including in a hidden
    // tab, and each refresh costs the server one Mongo contact lookup plus a
    // presence read for every contact. Polling only while the tab is actually
    // visible, at 60s, keeps the self-correcting behaviour while removing the
    // steady-state cost; a tab returning to the foreground refreshes
    // immediately rather than waiting out the interval.
    useEffect(() => {
        if (!wsConnected) return;

        const refresh = () => {
            if (document.visibilityState !== "visible") return;
            sendWs({ type: "dm:presence:refresh" });
        };

        const onVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };

        refresh();
        const interval = setInterval(refresh, 60000);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [wsConnected, sendWs]);

    const handlePickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = "";
        if (!files.length) return;
        setAttachmentError("");
        setPendingFiles((prev) => [...prev, ...files]);
    };

    const removePendingFile = (index: number) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSend = async () => {
        const text = input.trim();
        const files = pendingFiles;
        if ((!text && !files.length) || !activeThreadId || sending || !wsConnected) return;

        setSending(true);
        setAttachmentError("");

        let attachments: Attachment[] = [];
        if (files.length) {
            setUploadingAttachments(true);
            try {
                const form = new FormData();
                files.forEach((f) => form.append("files", f));
                const res = await apiClient.post<{ attachments: Attachment[] }>(
                    `/api/dm/threads/${activeThreadId}/attachments`,
                    form,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                attachments = res.data.attachments;
            } catch (err: unknown) {
                const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
                setAttachmentError(typeof detail === "string" ? detail : "Failed to upload attachment(s)");
                setUploadingAttachments(false);
                setSending(false);
                return;
            }
            setUploadingAttachments(false);
        }

        setInput("");
        setPendingFiles([]);
        emitTyping(false);

        // Optimistic add
        const clientMsgId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const tempMsg: Message = {
            id: clientMsgId,
            thread_id: activeThreadId,
            sender_id: userId,
            sender_name: user?.name ?? "You",
            sender_role: user?.role ?? "buyer",
            content: text,
            created_at: new Date().toISOString(),
            read: false,
            client_msg_id: clientMsgId,
            status: "pending",
            attachments,
        };
        setMessages((prev) => [...prev, tempMsg]);
        setTimeout(scrollToBottom, 50);
        const lastMessagePreview = text || (attachments.length ? `📎 ${attachments.length} attachment(s)` : "");
        setThreads((prev) =>
            sortByLatest(
                prev.map((t) =>
                    t.id === activeThreadId
                        ? {
                              ...t,
                              last_message: lastMessagePreview,
                              last_message_at: tempMsg.created_at,
                              updated_at: tempMsg.created_at,
                          }
                        : t,
                ),
            ),
        );

        sendWs({
            type: "dm:message:send",
            thread_id: activeThreadId,
            content: text,
            client_msg_id: clientMsgId,
            attachments,
        });
        setSending(false);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const emitTyping = useCallback((isTyping: boolean) => {
        if (!activeThreadId || !wsConnected) return;
        sendWs({
            type: "dm:typing",
            thread_id: activeThreadId,
            is_typing: isTyping,
        });
    }, [activeThreadId, wsConnected, sendWs]);

    const selectThread = (tid: string) => {
        setActiveThreadId(tid);
        navigate(`/dm/${tid}`, { replace: true });
        setShowSidebar(false);
    };

    const otherName = (t: Thread) =>
        t.buyer_id === userId ? t.seller_name : t.buyer_name;

    const chatStatusText = useMemo(() => {
        if (!wsConnected) return "Connecting...";
        if (typingName) return `${typingName} is typing...`;
        if (activeThread) {
            const otherUserId = activeThread.buyer_id === userId
                ? activeThread.seller_id
                : activeThread.buyer_id;
            if (onlineUserIds.has(otherUserId)) return "Online";
        }
        return "Offline";
    }, [wsConnected, typingName, activeThread, userId, onlineUserIds]);

    const formatTime = (iso?: string) => formatISTChatListTime(iso ?? "");

    return (
        <div className="dm-root">
            <div className={`dm-sidebar ${showSidebar ? "dm-sidebar--open" : "dm-sidebar--closed"}`}>
                <div className="dm-sidebar-header">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-lg" aria-hidden="true">💬</span>
                            <h2 className="dm-sidebar-title">Messages</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={() => navigate("/chat")} className="dm-nav-btn">
                                ✈️ AI Chat
                            </button>
                            <NotificationBell />
                        </div>
                    </div>
                    {error ? (
                        <div className="mt-2">
                            <p className="text-xs text-red-400">{error}</p>
                            {error.toLowerCase().includes("pending admin verification") && (
                                <button
                                    type="button"
                                    onClick={() => setVerificationModalMessage(error)}
                                    className="mt-1 text-xs text-indigo-400 hover:text-indigo-300 underline"
                                >
                                    Request Verification
                                </button>
                            )}
                        </div>
                    ) : null}
                </div>

                <div className="dm-thread-list">
                    {loadingThreads ? (
                        <div className="dm-empty">Loading conversations…</div>
                    ) : threads.length === 0 ? (
                        <div className="dm-empty">
                            <div className="mb-2 text-3xl opacity-80" aria-hidden="true">💬</div>
                            <p className="text-sm text-slate-400">No conversations yet.</p>
                            <p className="mt-1 text-xs text-slate-500">Use AI Chat to connect with sellers.</p>
                        </div>
                    ) : (
                        threads.map((t) => (
                            <div
                                key={t.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => selectThread(t.id)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        selectThread(t.id);
                                    }
                                }}
                                className={`dm-thread-item ${activeThreadId === t.id ? "dm-thread-item--active" : ""}`}
                            >
                                <div className="flex gap-2.5">
                                    <div className="dm-avatar">{(otherName(t)?.[0] ?? "?").toUpperCase()}</div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className={`truncate text-sm font-semibold ${activeThreadId === t.id ? "text-indigo-200" : "text-slate-300"}`}>
                                                {otherName(t)}
                                            </span>
                                            <span className="shrink-0 text-[10px] text-slate-500">
                                                {formatTime(t.last_message_at ?? t.updated_at)}
                                            </span>
                                        </div>
                                        {t.deal_title ? (
                                            <p className="mt-0.5 truncate text-[11px] font-medium text-indigo-300">
                                                ✈️ {t.deal_title}
                                            </p>
                                        ) : null}
                                        <div className="mt-0.5 flex items-center justify-between gap-2">
                                            {t.last_message ? (
                                                <p className="min-w-0 flex-1 truncate text-xs text-slate-500">{t.last_message}</p>
                                            ) : (
                                                <span />
                                            )}
                                            {t.unread_count > 0 ? (
                                                <span className="dm-unread">{t.unread_count}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="dm-main">
                <div className="dm-chat-header">
                    <button
                        type="button"
                        onClick={() => setShowSidebar(!showSidebar)}
                        className="dm-toggle-btn"
                        title={showSidebar ? "Hide sidebar" : "Show sidebar"}
                        aria-label={showSidebar ? "Hide sidebar" : "Show sidebar"}
                    >
                        ☰
                    </button>
                    {activeThread ? (
                        <div className="flex flex-1 items-center gap-2.5">
                            <div className="dm-avatar h-9 w-9 text-sm">
                                {(otherName(activeThread)?.[0] ?? "?").toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-100">{otherName(activeThread)}</p>
                                {activeThread.deal_title ? (
                                    <p className="truncate text-[11px] text-indigo-300">✈️ {activeThread.deal_title}</p>
                                ) : null}
                                <p
                                    className={`text-[10px] ${
                                        chatStatusText === "Online"
                                            ? "text-emerald-400"
                                            : chatStatusText.includes("typing")
                                              ? "text-indigo-300"
                                              : "text-slate-500"
                                    }`}
                                >
                                    {chatStatusText === "Online"
                                        ? "● Online"
                                        : chatStatusText.includes("typing")
                                          ? `✎ ${chatStatusText}`
                                          : wsConnected
                                            ? "○ Offline"
                                            : "Connecting…"}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500">Select a conversation</p>
                    )}
                </div>

                <div className="dm-messages">
                    {!activeThreadId ? (
                        <div className="dm-empty mt-16">
                            <div className="mb-4 text-5xl opacity-60" aria-hidden="true">💬</div>
                            <p className="text-base font-medium text-slate-400">Select a conversation to start chatting</p>
                            <p className="mt-2 text-sm text-slate-500">
                                Or go to{" "}
                                <button type="button" className="font-medium text-indigo-300 hover:underline" onClick={() => navigate("/chat")}>
                                    AI Chat
                                </button>{" "}
                                to discover deals
                            </p>
                        </div>
                    ) : loadingMessages ? (
                        <div className="dm-empty">Loading messages…</div>
                    ) : messages.length === 0 ? (
                        <div className="dm-empty">No messages yet. Start the conversation!</div>
                    ) : (
                        <>
                            {activeThread?.deal_title ? (
                                <div className="mb-4 rounded-xl border border-indigo-500/25 bg-indigo-500/10 px-4 py-2 text-center text-xs">
                                    <span className="text-slate-500">Conversation about </span>
                                    <span className="font-semibold text-indigo-200">{activeThread.deal_title}</span>
                                </div>
                            ) : null}

                            {messages.map((msg) => {
                                const isMe = msg.sender_id === userId;
                                return (
                                    <div key={msg.id} className={`flex gap-1.5 ${isMe ? "justify-end" : "justify-start"}`}>
                                        {!isMe ? (
                                            <div className="dm-avatar h-7 w-7 text-[10px] bg-white/10">
                                                {(msg.sender_name?.[0] ?? "?").toUpperCase()}
                                            </div>
                                        ) : null}
                                        <div className={isMe ? "dm-msg dm-msg--mine" : "dm-msg dm-msg--theirs"}>
                                            {!isMe ? (
                                                <p className="mb-1 text-[11px] font-semibold text-indigo-300">{msg.sender_name}</p>
                                            ) : null}
                                            {msg.content ? (
                                                <div className="whitespace-pre-wrap">{msg.content}</div>
                                            ) : null}
                                            {msg.attachments && msg.attachments.length > 0 ? (
                                                <div className="mt-1 flex flex-col gap-1">
                                                    {msg.attachments.map((att) => (
                                                        <ChatAttachment key={att.s3_key} attachment={att} />
                                                    ))}
                                                </div>
                                            ) : null}
                                            <div className={`mt-1 flex items-center justify-end gap-1 text-[10px] ${isMe ? "text-white/50" : "text-slate-500"}`}>
                                                {formatTime(msg.created_at)}
                                                {isMe && msg.status === "pending" ? (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-label="Sending" className="opacity-50"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
                                                ) : isMe && msg.status === "failed" ? (
                                                    <span className="text-red-300">⚠</span>
                                                ) : isMe && msg.status === "read" ? (
                                                    /* Blue double tick = read */
                                                    <svg width="16" height="10" viewBox="0 0 16 10" fill="none" aria-label="Read" className="text-blue-400">
                                                        <path d="M1 5l3 3 5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M6 5l3 3 5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                ) : isMe && msg.status === "delivered" ? (
                                                    /* Double grey tick = delivered */
                                                    <svg width="16" height="10" viewBox="0 0 16 10" fill="none" aria-label="Delivered" className="text-white/50">
                                                        <path d="M1 5l3 3 5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                        <path d="M6 5l3 3 5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                ) : isMe && msg.status === "sent" ? (
                                                    /* Single grey tick = sent, not yet delivered */
                                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-label="Sent" className="text-white/50">
                                                        <path d="M1 5l3 3 5-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {activeThreadId ? (
                    <div className="flex flex-col gap-1.5">
                        {attachmentError ? (
                            <div className="text-[11px] text-red-400">{attachmentError}</div>
                        ) : null}
                        {pendingFiles.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {pendingFiles.map((file, index) => (
                                    <div
                                        key={`${file.name}-${index}`}
                                        className="flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-[11px] text-white/80"
                                    >
                                        <span className="max-w-[140px] truncate">{file.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => removePendingFile(index)}
                                            aria-label={`Remove ${file.name}`}
                                            className="text-white/60 hover:text-white"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        <div className="dm-input-bar flex gap-2.5">
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={ACCEPTED_ATTACHMENT_TYPES}
                            onChange={handlePickFiles}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingAttachments}
                            className="dm-send-btn"
                            aria-label="Attach files"
                            title="Attach images or documents"
                        >
                            {uploadingAttachments ? (
                                <span className="text-xs">…</span>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                                </svg>
                            )}
                        </button>
                        <div className="dm-input-inner flex-1">
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setInput(value);
                                    emitTyping(value.length > 0);
                                    if (typingStopTimeoutRef.current) {
                                        clearTimeout(typingStopTimeoutRef.current);
                                    }
                                    typingStopTimeoutRef.current = setTimeout(() => {
                                        emitTyping(false);
                                    }, 1800);
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder="Type a message…"
                                rows={1}
                                className="dm-input"
                                onInput={(e) => {
                                    const el = e.currentTarget;
                                    el.style.height = "auto";
                                    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={(!input.trim() && !pendingFiles.length) || sending || !wsConnected || uploadingAttachments}
                            className="dm-send-btn"
                            aria-label="Send message"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                            </svg>
                        </button>
                        </div>
                    </div>
                ) : null}
            </div>
            {verificationModalMessage ? (
                <VerificationRequiredModal
                    message={verificationModalMessage}
                    onClose={() => setVerificationModalMessage("")}
                />
            ) : null}
        </div>
    );
}
